import React, { useEffect, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";

import { getVisualQualityGateContractV1 } from "../../lib/brandStudioApi";

const REASON_LABELS = {
  pseudo_text: "Псевдотекст",
  invented_logo: "Выдуманный логотип",
  invented_monogram: "Выдуманная монограмма",
  identity_hallucination: "Галлюцинация айдентики",
  mutant_geometry: "Мутировавшая геометрия",
  implausible_object: "Неправдоподобный объект",
  severe_visual_artifact: "Сильные визуальные артефакты",
  wrong_workflow: "Неверный workflow",
  locked_rule_violation: "Нарушение обязательного правила",
  visual_style_mismatch: "Несоответствие visual style",
};

const WORKFLOW_LABELS = {
  photography_direction: "Фотография",
  material_direction: "Материалы / фактуры",
  graphic_presentation_direction: "Графический язык / подача",
};

export default function VisualQualityGateOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getVisualQualityGateContractV1()
      .then((value) => {
        if (cancelled) return;
        setResult(value);
        setStatus("ready");
      })
      .catch((problem) => {
        if (cancelled) return;
        setError(
          problem?.response?.data?.detail ||
            "Не удалось открыть контракт Visual Quality Gate.",
        );
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const contract = result?.contract;

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="visual-quality-gate-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · QG1.1
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Visual Quality Gate — контракт проверки
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Этот слой только фиксирует, что должен проверить Quality Gate. Изображение
        никуда не отправляется, evaluator ещё не выбран, вызовов и расходов нет.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Собираю контракт проверки…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {String(error)}
        </div>
      )}

      {status === "ready" && result?.result !== "ready" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {result?.reason || "Quality Gate пока не готов."}
          {!!result?.blocking_reasons?.length && (
            <div className="mt-1 text-[10px]">
              Блокирует: {result.blocking_reasons.join(", ")}
            </div>
          )}
        </div>
      )}

      {contract && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} />
              готов к подключению evaluator
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              {WORKFLOW_LABELS[contract.workflow_type] || contract.workflow_type}
              {" · "}контракт {contract.contract_fingerprint.slice(0, 12)}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Источник
              </div>
              <div className="mt-2 text-[10.5px] leading-5 text-[#655141]">
                Technical status: успешно
                <br />
                Пакет: текущий
                <br />
                Media: private
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Evaluator
              </div>
              <div className="mt-2 text-[10.5px] leading-5 text-[#655141]">
                Provider: не выбран
                <br />
                Model: не выбрана
                <br />
                Binary egress: не разрешён
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Стоимость
              </div>
              <div className="mt-2 text-[10.5px] leading-5 text-[#655141]">
                Evaluation calls: {contract.evaluation_call_count}
                <br />
                Estimated cost: $0
                <br />
                Verdict persisted: нет
              </div>
            </article>
          </div>

          <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#927A5B]">
              <ShieldCheck size={14} />
              10 обязательных reject checks
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {contract.common_checks.map((check) => (
                <div
                  key={check.reason}
                  className="rounded-[10px] border border-[#E8DDCF] bg-[#FFFDF9] px-3 py-2 text-[10px] text-[#66513F]"
                >
                  {REASON_LABELS[check.reason] || check.reason}
                  {" · "}ожидает оценки
                </div>
              ))}
            </div>
          </article>

          <div className="grid gap-3 lg:grid-cols-2">
            <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Workflow requirements
              </div>
              <div className="mt-3 space-y-2 text-[10px] leading-4 text-[#66513F]">
                {contract.workflow_requirements.map((check) => (
                  <div key={check.check_id}>• {check.text}</div>
                ))}
              </div>
            </article>

            <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Reject conditions
              </div>
              <div className="mt-3 space-y-2 text-[10px] leading-4 text-[#66513F]">
                {contract.workflow_reject_conditions.map((check) => (
                  <div key={check.check_id}>• {check.text}</div>
                ))}
              </div>
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
